<template>
  <section class="card builder">
    <div class="step">
      <div class="step-head">
        <div class="step-num">1</div>
        <h2>Страница, куда ведём</h2>
        <span class="hint">адрес без меток</span>
      </div>
      <div class="field">
        <select class="proto" :value="protocol" aria-label="Протокол" @change="onProtocol">
          <option value="https://">https://</option>
          <option value="http://">http://</option>
        </select>
        <div class="sep"></div>
        <input
          :value="path"
          placeholder="get-nechto.ru/…"
          spellcheck="false"
          aria-label="Адрес страницы"
          @input="onPath"
        >
      </div>
      <div class="helper">Ссылку можно вставить целиком — протокол и лишние пробелы уберём сами.</div>
    </div>

    <div class="step">
      <div class="step-head">
        <div class="step-num">2</div>
        <h2>Куда относим лиды</h2>
        <span class="hint">источник и площадка</span>
      </div>
      <div class="grid3">
        <div>
          <span class="lbl">Тип трафика</span>
          <SelectVue
            :modelValue="lead"
            :options="leads"
            filter
            appendTo="body"
            placeholder="Выберите"
            emptyMessage="Нет данных"
            emptyFilterMessage="Ничего не найдено"
            class="utm-select"
            @update:modelValue="onLead"
          />
        </div>
        <div>
          <span class="lbl">Источник</span>
          <SelectVue
            :modelValue="origin"
            :options="originOptions"
            filter
            appendTo="body"
            placeholder="Выберите"
            emptyMessage="Нет данных"
            emptyFilterMessage="Ничего не найдено"
            class="utm-select"
            @update:modelValue="onOrigin"
          />
        </div>
        <div>
          <span class="lbl">Площадка</span>
          <SelectVue
            :modelValue="platform"
            :options="platformOptions"
            filter
            appendTo="body"
            placeholder="Выберите"
            emptyMessage="Нет данных"
            emptyFilterMessage="Ничего не найдено"
            class="utm-select"
            @update:modelValue="onPlatform"
          />
        </div>
      </div>

      <UtmPopularEvents
        class="popular"
        :events="popular"
        :selected="[event?.id]"
        @pick="pickPopular"
      />

      <div class="events-head">
        <span class="cap">Событие</span>
        <span class="count">{{ filteredEvents.length }} из {{ events.length }}</span>
        <div class="search">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
          <input v-model="search" placeholder="Поиск события" aria-label="Поиск события">
        </div>
      </div>

      <div v-if="!events.length" class="empty">Для этой площадки событий нет. Добавьте его в справочниках.</div>
      <div v-else-if="!filteredEvents.length" class="empty">Ничего не найдено</div>
      <div v-else class="event-grid">
        <button
          v-for="row in filteredEvents"
          :key="row.id"
          type="button"
          class="event"
          :class="{ on: event && event.id === row.id }"
          @click="selectEvent(row)"
        >
          <span class="dot"></span>
          <span class="body">
            <span class="name">{{ row.event }}</span>
            <span class="meta">{{ row.utm_source || '—' }} · {{ row.utm_medium || '—' }}</span>
          </span>
        </button>
      </div>
    </div>

    <div class="step">
      <div class="step-head">
        <div class="step-num">3</div>
        <h2>Метки ссылки</h2>
        <span class="hint">можно править любую</span>
      </div>

      <div class="param">
        <div class="param-name"><code>utm_source</code><span class="sub">источник трафика</span></div>
        <div class="ctl">
          <input class="input" :value="source" readonly aria-label="utm_source">
          <span v-if="source" class="badge">из события</span>
        </div>
      </div>

      <div class="param">
        <div class="param-name"><code>utm_medium</code><span class="sub">канал продвижения</span></div>
        <div class="ctl">
          <input class="input" :value="medium" aria-label="utm_medium" @input="onMedium">
          <span v-if="fromEvent.utm_medium" class="badge">из события</span>
          <button v-if="event && medium !== eventMedium" type="button" class="btn sm ghost" @click="restoreMedium">вернуть</button>
        </div>
      </div>

      <div class="param">
        <div class="param-name"><code>utm_campaign</code><span class="sub">кампания или событие</span></div>
        <div class="ctl">
          <SelectVue
            :modelValue="campaign"
            :options="campaigns"
            optionLabel="name"
            optionValue="value"
            editable
            filter
            appendTo="body"
            placeholder="Выберите или введите"
            emptyFilterMessage="Ничего не найдено"
            class="utm-select"
            @update:modelValue="onCampaign"
          />
          <span v-if="fromEvent.utm_campaign" class="badge">из события</span>
        </div>
      </div>

      <div class="param">
        <div class="param-name"><code>utm_content</code><span class="sub">вариант материала</span></div>
        <div class="ctl">
          <SelectVue
            :modelValue="content"
            :options="contents"
            optionLabel="name"
            optionValue="value"
            editable
            filter
            appendTo="body"
            placeholder="Выберите или введите"
            emptyFilterMessage="Ничего не найдено"
            class="utm-select"
            @update:modelValue="onContent"
          />
          <span v-if="fromEvent.utm_content" class="badge">из события</span>
        </div>
      </div>

      <div class="param">
        <div class="param-name"><code>utm_term</code><span class="sub">дата запуска</span></div>
        <div class="ctl">
          <DatePicker
            :modelValue="termDate"
            dateFormat="dd.mm.yy"
            showIcon
            appendTo="body"
            class="term-picker"
            placeholder="дд.мм.гг"
            @update:modelValue="onTerm"
          />
          <span class="badge gray">необязательно</span>
        </div>
      </div>

      <p v-if="translitBits.length" class="translit">
        В ссылке будет:
        <template v-for="(bit, index) in translitBits" :key="bit.key">
          <span v-if="index">, </span>{{ bit.key }}=<b>{{ bit.value }}</b>
        </template>
      </p>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import UtmPopularEvents from './UtmPopularEvents.vue'
import {
  formatTermDate,
  hasCyrillic,
  normalizeUrlInput,
  parseTermDate,
  transliterate,
  valueFromEvent,
} from './utm-utils'

const props = defineProps({
  origins: { type: Array, default: () => [] },
  campaigns: { type: Array, default: () => [] },
  contents: { type: Array, default: () => [] },
  popular: { type: Array, default: () => [] },
})

const emit = defineEmits(['change'])

const protocol = ref('https://')
const path = ref('')
const lead = ref('')
const origin = ref('')
const platform = ref('')
const event = ref(null)
const medium = ref('')
const campaign = ref('')
const content = ref('')
const termDate = ref(null)
const search = ref('')
const fromEvent = ref({
  utm_source: false,
  utm_medium: false,
  utm_campaign: false,
  utm_content: false,
  utm_term: false,
})

function uniq(rows, key) {
  const seen = []
  rows.forEach((row) => {
    const value = String(row?.[key] ?? '').trim()
    if (value && !seen.includes(value)) seen.push(value)
  })
  return seen
}

const leads = computed(() => uniq(props.origins, 'lead'))
const originOptions = computed(() => uniq(
  props.origins.filter((row) => String(row.lead ?? '').trim() === lead.value),
  'origin'
))
const platformOptions = computed(() => uniq(
  props.origins.filter((row) => String(row.lead ?? '').trim() === lead.value && String(row.origin ?? '').trim() === origin.value),
  'platform'
))
const events = computed(() => props.origins.filter((row) =>
  String(row.lead ?? '').trim() === lead.value
  && String(row.origin ?? '').trim() === origin.value
  && String(row.platform ?? '').trim() === platform.value
))
const filteredEvents = computed(() => {
  const query = search.value.trim().toLowerCase()
  if (!query) return events.value
  return events.value.filter((row) => [row.event, row.origin, row.platform]
    .some((part) => String(part ?? '').toLowerCase().includes(query)))
})
const source = computed(() => valueFromEvent(event.value?.utm_source))
const eventMedium = computed(() => valueFromEvent(event.value?.utm_medium))

const translitBits = computed(() => {
  const fields = {
    utm_medium: medium.value,
    utm_campaign: campaign.value,
    utm_content: content.value,
    utm_term: formatTermDate(termDate.value),
  }
  return Object.keys(fields)
    .filter((key) => hasCyrillic(fields[key]))
    .map((key) => ({ key, value: transliterate(fields[key]) }))
})

function clearMedium() {
  medium.value = ''
  fromEvent.value.utm_medium = false
  fromEvent.value.utm_source = false
}

function applyKey(raw, write, key) {
  const value = valueFromEvent(raw)
  write(value)
  fromEvent.value[key] = Boolean(value)
}

function pickPopular(row) {
  lead.value = String(row?.lead ?? '').trim()
  origin.value = String(row?.origin ?? '').trim()
  platform.value = String(row?.platform ?? '').trim()
  selectEvent(row)
}

function selectEvent(row) {
  event.value = row
  applyKey(row?.utm_medium, (value) => { medium.value = value }, 'utm_medium')
  applyKey(row?.utm_campaign, (value) => { campaign.value = value }, 'utm_campaign')
  applyKey(row?.utm_content, (value) => { content.value = value }, 'utm_content')
  const rawTerm = row?.utm_term
  const parsed = rawTerm && !String(rawTerm).startsWith('.') ? parseTermDate(rawTerm) : null
  termDate.value = parsed
  fromEvent.value.utm_term = Boolean(parsed)
  fromEvent.value.utm_source = Boolean(valueFromEvent(row?.utm_source))
}

function onProtocol(e) {
  protocol.value = e.target.value
}

function onPath(e) {
  const next = normalizeUrlInput(e.target.value, protocol.value)
  protocol.value = next.protocol
  path.value = next.value
}

function onLead(value) {
  lead.value = value || ''
  origin.value = originOptions.value[0] || ''
  platform.value = platformOptions.value[0] || ''
  event.value = null
  clearMedium()
}

function onOrigin(value) {
  origin.value = value || ''
  platform.value = platformOptions.value[0] || ''
  event.value = null
  clearMedium()
}

function onPlatform(value) {
  platform.value = value || ''
  event.value = null
  clearMedium()
}

function onMedium(e) {
  medium.value = e.target.value
  const from = valueFromEvent(event.value?.utm_medium)
  fromEvent.value.utm_medium = Boolean(from) && medium.value === from
}

function restoreMedium() {
  medium.value = valueFromEvent(event.value?.utm_medium)
  fromEvent.value.utm_medium = Boolean(medium.value)
}

function onCampaign(value) {
  campaign.value = value || ''
  const from = valueFromEvent(event.value?.utm_campaign)
  fromEvent.value.utm_campaign = Boolean(from) && campaign.value === from
}

function onContent(value) {
  content.value = value || ''
  const from = valueFromEvent(event.value?.utm_content)
  fromEvent.value.utm_content = Boolean(from) && content.value === from
}

function onTerm(value) {
  termDate.value = value
  fromEvent.value.utm_term = false
}

function ensureSelection() {
  if (!props.origins.length) return
  let dropEvent = false
  if (!leads.value.includes(lead.value)) {
    lead.value = leads.value[0] || ''
    origin.value = ''
    platform.value = ''
    dropEvent = true
  }
  if (!originOptions.value.includes(origin.value)) {
    origin.value = originOptions.value[0] || ''
    platform.value = ''
    dropEvent = true
  }
  if (!platformOptions.value.includes(platform.value)) {
    platform.value = platformOptions.value[0] || ''
    dropEvent = true
  }
  if (dropEvent) {
    event.value = null
    clearMedium()
    return
  }
  if (!event.value) return
  const fresh = events.value.find((row) => row.id === event.value.id)
  if (!fresh) {
    event.value = null
    clearMedium()
    return
  }
  event.value = fresh
  if (fromEvent.value.utm_medium) {
    medium.value = valueFromEvent(fresh.utm_medium)
    fromEvent.value.utm_medium = Boolean(medium.value)
  }
  if (fromEvent.value.utm_campaign) {
    campaign.value = valueFromEvent(fresh.utm_campaign)
    fromEvent.value.utm_campaign = Boolean(campaign.value)
  }
  if (fromEvent.value.utm_content) {
    content.value = valueFromEvent(fresh.utm_content)
    fromEvent.value.utm_content = Boolean(content.value)
  }
  fromEvent.value.utm_source = Boolean(valueFromEvent(fresh.utm_source))
}

watch(() => props.origins, ensureSelection, { immediate: true })

watch(
  [protocol, path, lead, origin, platform, event, medium, campaign, content, termDate, source],
  () => {
    emit('change', {
      protocol: protocol.value,
      path: path.value,
      lead: lead.value,
      origin: origin.value,
      platform: platform.value,
      event: event.value,
      fields: {
        utm_source: source.value,
        utm_medium: medium.value,
        utm_campaign: campaign.value || '',
        utm_content: content.value || '',
        utm_term: formatTermDate(termDate.value),
      },
      fromEvent: {
        utm_source: Boolean(source.value),
        utm_medium: fromEvent.value.utm_medium,
        utm_campaign: fromEvent.value.utm_campaign,
        utm_content: fromEvent.value.utm_content,
        utm_term: fromEvent.value.utm_term,
      },
    })
  },
  { immediate: true }
)
</script>

<style scoped>
.builder {
  --border: #e6e8ec;
  --border-strong: #d5d9e0;
  --text: #14181f;
  --muted: #6b7383;
  --muted-2: #98a0ae;
  --accent: #676B52;
  --accent-soft: #eef0e6;
  --accent-line: #d8dcc8;
  --mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  --shadow: 0 1px 2px rgba(16, 24, 40, .04), 0 8px 24px -12px rgba(16, 24, 40, .12);
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: var(--shadow);
  padding: 6px 26px 24px;
  min-width: 0;
}
.step { padding: 22px 0; border-bottom: 1px solid #eef0f3; }
.step:last-child { border-bottom: 0; padding-bottom: 6px; }
.step-head { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.step-num {
  width: 22px; height: 22px; border-radius: 50%; background: var(--accent-soft); color: var(--accent);
  display: grid; place-items: center; font-size: 11px; font-weight: 700; flex: 0 0 auto;
}
.step-head h2 { margin: 0; font-size: 15px; font-weight: 600; letter-spacing: -.01em; }
.step-head .hint { margin-left: auto; font-size: 12px; color: var(--muted-2); }
.field {
  display: flex; align-items: center; gap: 10px; background: #fff; border: 1px solid var(--border-strong);
  border-radius: 12px; padding: 0 12px; height: 46px;
}
.field:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(103, 107, 82, .12); }
.field input { flex: 1 1 auto; border: 0; outline: 0; background: transparent; font-size: 14px; min-width: 0; color: var(--text); }
.field input::placeholder { color: var(--muted-2); }
.proto {
  border: 0; outline: 0; background: transparent; font-size: 14px; font-weight: 500; color: var(--muted);
  appearance: none; padding: 4px 18px 4px 0;
  background-image: linear-gradient(45deg, transparent 50%, #98a0ae 50%), linear-gradient(135deg, #98a0ae 50%, transparent 50%);
  background-position: calc(100% - 9px) 52%, calc(100% - 5px) 52%; background-size: 4px 4px; background-repeat: no-repeat;
}
.sep { width: 1px; height: 22px; background: var(--border); flex: 0 0 auto; }
.helper { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--muted-2); margin-top: 8px; }
.grid3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.popular { display: block; margin-top: 17px; }
.lbl { display: block; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; color: var(--muted-2); margin: 0 0 6px 2px; }
.events-head { display: flex; align-items: center; gap: 10px; margin: 18px 0 10px; }
.events-head .cap { font-size: 12px; font-weight: 600; color: var(--muted); }
.events-head .count { font-size: 11px; color: var(--muted-2); }
.events-head .search {
  margin-left: auto; display: flex; align-items: center; gap: 6px; background: #f6f7f9; border: 1px solid var(--border);
  border-radius: 9px; padding: 0 10px; height: 32px; width: 220px;
}
.events-head .search input { border: 0; outline: 0; background: transparent; font-size: 12.5px; width: 100%; color: var(--text); }
.icon { width: 14px; height: 14px; color: var(--muted-2); flex: 0 0 auto; }
.event-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.event {
  display: flex; align-items: center; gap: 10px; border: 1px solid var(--border); border-radius: 11px; padding: 10px 12px;
  background: #fff; text-align: left; font: inherit; color: inherit; cursor: pointer;
}
.event:hover { border-color: var(--border-strong); background: #fcfcfd; }
.event.on { border-color: var(--accent); background: var(--accent-soft); box-shadow: inset 0 0 0 1px var(--accent); }
.event .dot { width: 16px; height: 16px; border-radius: 50%; border: 1.5px solid #cfd4dc; flex: 0 0 auto; display: grid; place-items: center; background: #fff; }
.event.on .dot { border-color: var(--accent); background: var(--accent); }
.event.on .dot::after { content: ''; width: 6px; height: 6px; border-radius: 50%; background: #fff; }
.event .body { min-width: 0; }
.event .name { display: block; font-size: 13px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.event .meta { display: block; font-family: var(--mono); font-size: 11px; color: var(--muted-2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 2px; }
.event.on .meta { color: #7c8064; }
.empty { color: var(--muted-2); font-size: 13px; padding: 8px 2px 4px; }
.param { display: grid; grid-template-columns: 168px minmax(0, 1fr); gap: 14px; align-items: start; padding: 9px 0; }
.param + .param { border-top: 1px dashed #eef0f3; }
.param-name { padding-top: 10px; }
.param-name code { font-family: var(--mono); font-size: 12px; color: var(--text); background: #f3f4f7; border-radius: 6px; padding: 2px 6px; }
.param-name .sub { display: block; font-size: 11.5px; color: var(--muted-2); margin-top: 5px; }
.ctl { display: flex; align-items: center; gap: 8px; min-width: 0; }
.ctl .utm-select, .ctl .term-picker { flex: 1 1 auto; min-width: 0; }
.input {
  height: 42px; border: 1px solid var(--border-strong); border-radius: 10px; background: #fff; padding: 0 12px;
  width: 100%; font-size: 13.5px; outline: 0; color: var(--text);
}
.input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(103, 107, 82, .12); }
.input[readonly] { background: #f8f9fb; color: var(--muted); }
.badge {
  font-size: 10.5px; font-weight: 600; color: var(--accent); background: var(--accent-soft); border: 1px solid var(--accent-line);
  border-radius: 6px; padding: 2px 6px; white-space: nowrap;
}
.badge.gray { color: var(--muted); background: #f3f4f7; border-color: var(--border); }
.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px; border-radius: 10px;
  border: 1px solid transparent; background: transparent; padding: 9px 14px; font-size: 13px; font-weight: 500;
  color: var(--muted); cursor: pointer; font-family: inherit;
}
.btn:hover { background: #eceef2; color: var(--text); }
.btn.sm { padding: 6px 10px; font-size: 12px; border-radius: 8px; flex: 0 0 auto; }
.translit { font-size: 11.5px; color: var(--muted-2); margin: 8px 0 0; }
.translit b { font-family: var(--mono); font-weight: 500; color: var(--accent); }
:deep(.utm-select.p-select) {
  width: 100%; height: 44px; border-radius: 10px; border: 1px solid #d5d9e0; background: #fff; align-items: center;
}
:deep(.utm-select .p-select-label) { font-size: 13.5px; padding-top: 0; padding-bottom: 0; display: flex; align-items: center; }
:deep(.utm-select.p-inputwrapper-focus),
:deep(.utm-select.p-select:focus-within) { border-color: #676B52; box-shadow: 0 0 0 3px rgba(103, 107, 82, .12); }
:deep(.term-picker) { width: 100%; }
:deep(.term-picker .p-inputtext) {
  height: 42px; border-radius: 10px; border-color: #d5d9e0; font-size: 13.5px; width: 100%;
}
:deep(.term-picker .p-inputtext:focus) { border-color: #676B52; box-shadow: 0 0 0 3px rgba(103, 107, 82, .12); }
@media (max-width: 760px) {
  .builder { padding: 6px 14px 16px; }
  .grid3, .event-grid, .param { grid-template-columns: 1fr; }
  .events-head { flex-wrap: wrap; }
  .events-head .search { width: 100%; margin-left: 0; height: 44px; }
  .field, .btn, .event, .input, .proto { min-height: 44px; }
  .field input, .input, .proto, .events-head .search input { font-size: 16px; }
  :deep(.utm-select.p-select), :deep(.term-picker .p-inputtext) { min-height: 44px; font-size: 16px; }
  :deep(.utm-select .p-select-label), :deep(.term-picker .p-inputtext) { font-size: 16px; }
}
</style>
